import React, { useCallback, useEffect } from 'react';
import { ThemeProvider as AudiPlatformProvider } from '@oneaudi/unified-web-common';
import { useTranslation } from 'react-i18next';
// @ts-ignore
// eslint-disable-next-line import/no-relative-packages
import FocusTrap from '../../../../node_modules/focus-trap-react/dist/focus-trap-react'; // or the exact path to 'focus-trap-react' if it's different
import useInnerHeight from '../../hooks/useInnerHeight';
import useLockBodyScroll from '../../hooks/useLockBodyScroll';
import ModalPortal from './ModalPortal';
import { Overlay, ModalContainer } from './Modal.styles';

interface ModalProps {
  onClose: () => void;
  closableOnEscape: boolean;
  width?: string;
  children?: React.ReactNode | React.ReactNode[];
}

const Modal = React.forwardRef<HTMLDivElement | null, ModalProps>(
  ({ onClose, closableOnEscape, children, width }, ref) => {
    const { t } = useTranslation();
    const innerHeight = useInnerHeight();

    useLockBodyScroll(true);

    const handleKeyPress = useCallback(
      (e: KeyboardEvent) => {
        const keyPressed: { [key: string]: () => void } = {
          Escape: () => {
            if (closableOnEscape) onClose();
          },
        };

        if (Object.keys(keyPressed).includes(e.key)) {
          keyPressed[e.key]();
        }
      },
      [closableOnEscape, onClose],
    );

    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        if (e.target !== e.currentTarget) return;
        onClose();
      },
      [onClose],
    );

    useEffect(() => {
      document.addEventListener('keydown', handleKeyPress, { passive: true });

      return () => {
        document.removeEventListener('keydown', handleKeyPress);
      };
    }, [handleKeyPress]);

    return (
      <ModalPortal>
        <FocusTrap
          focusTrapOptions={{
            initialFocus: false,
            escapeDeactivates: true,
            clickOutsideDeactivates: true,
          }}
        >
          <Overlay
            // Change
            aria-label={t('dealerLocationSearch.map.modalBackdrop')}
            onClick={handleClick}
            tabIndex={0}
          >
            <ModalContainer
              role="dialog"
              // Change
              aria-label={t('dealerLocationSearch.map.modal')}
              aria-modal="true"
              data-modal-title={t('dealerLocationSearch.map.modal')}
              width={width}
              height={`${innerHeight}px`}
              ref={ref}
              tabIndex={0}
            >
              <AudiPlatformProvider>{children}</AudiPlatformProvider>
            </ModalContainer>
          </Overlay>
        </FocusTrap>
      </ModalPortal>
    );
  },
);

Modal.defaultProps = {
  width: undefined,
};

export default Modal;
